import { useRouteError, useNavigate } from 'react-router-dom';
import { Container, Title, Text, Button, Collapse, Code, Paper, Group, Box, ActionIcon, Tooltip, ScrollArea } from '@mantine/core';
import { useState } from 'react';
import { IconAlertTriangle, IconCopy, IconCheck, IconRefresh, IconHome, IconChevronDown, IconChevronUp } from '@tabler/icons-react';

const ErrorPage = () => {
    const error = useRouteError() as any;
    const navigate = useNavigate();
    const [opened, setOpened] = useState(false);
    const [copied, setCopied] = useState(false);

    // Extract meaningful error information
    const errorMessage = error?.message || error?.statusText || "An unexpected system error occurred";
    const errorStatus = error?.status || "Unknown Status";
    const errorStack = error?.stack || "No stack trace available";

    const errorDetails = `Status: ${errorStatus}\nMessage: ${errorMessage}\n\nStack Trace:\n${errorStack}`;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(errorDetails);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy error details", err);
        }
    };

    return (
        <Container size="md" style={{ minHeight: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
            <Paper 
                withBorder 
                shadow="xl" 
                p={{ base: 'lg', sm: 'xl' }} 
                radius="lg" 
                style={{ 
                    width: '100%', 
                    maxWidth: '650px',
                    background: 'var(--app-surface)',
                    borderColor: 'var(--app-border)',
                }}
            >
                <Box style={{ textAlign: 'center' }} mb="lg">
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--mantine-color-red-light)',
                        color: 'var(--mantine-color-red-6)',
                        marginBottom: '16px'
                    }}>
                        <IconAlertTriangle size={36} stroke={1.8} />
                    </div>
                    
                    <Title order={2} style={{ color: 'var(--app-text)', fontWeight: 800, fontSize: '24px', letterSpacing: '-0.02em' }} mb="xs">
                        Something went wrong
                    </Title>
                    <Text size="sm" c="dimmed" style={{ maxWidth: '400px', margin: '0 auto' }}>
                        The application encountered an unexpected error and could not complete your request.
                    </Text>
                </Box>

                <Paper 
                    withBorder 
                    p="md" 
                    radius="md" 
                    mb="xl" 
                    style={{ 
                        backgroundColor: 'var(--app-surface-muted)',
                        borderColor: 'var(--app-border)',
                    }}
                >
                    <Group justify="space-between" mb="xs" wrap="nowrap">
                        <Text size="xs" fw={700} style={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--mantine-color-red-6)' }}>
                            Error status: {errorStatus}
                        </Text>
                        <Tooltip label={copied ? "Copied!" : "Copy Error Details"} withArrow position="top">
                            <ActionIcon variant="subtle" color="gray" size="sm" onClick={handleCopy}>
                                {copied ? <IconCheck size={14} color="var(--mantine-color-green-6)" /> : <IconCopy size={14} />}
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                    <Text size="sm" fw={600} style={{ color: 'var(--app-text)', wordBreak: 'break-word' }}>
                        {errorMessage}
                    </Text>
                </Paper>

                <Group justify="center" gap="sm" mb="xl">
                    <Button 
                        variant="filled" 
                        color="blue" 
                        leftSection={<IconRefresh size={16} />}
                        onClick={() => navigate(0)}
                    >
                        Try Again
                    </Button>
                    <Button 
                        variant="outline" 
                        color="gray" 
                        leftSection={<IconHome size={16} />}
                        onClick={() => navigate('/')}
                    >
                        Go Home
                    </Button>
                </Group>

                <Box style={{ borderTop: '1px solid var(--app-border)', paddingTop: '16px' }}>
                    <Button
                        variant="subtle"
                        color="gray"
                        fullWidth
                        size="xs"
                        rightSection={opened ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                        onClick={() => setOpened((o) => !o)}
                        style={{ justifyContent: 'space-between', padding: '0 8px' }}
                    >
                        Technical Details (for Developers)
                    </Button>
                    
                    <Collapse expanded={opened} mt="md">
                        <Paper withBorder p="sm" radius="md" style={{ backgroundColor: 'var(--app-bg)', borderColor: 'var(--app-border)' }}>
                            <Box style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                                <Button 
                                    size="xs" 
                                    variant="subtle" 
                                    color="gray" 
                                    leftSection={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                                    onClick={handleCopy}
                                >
                                    {copied ? "Copied" : "Copy details"}
                                </Button>
                            </Box>
                            <ScrollArea style={{ height: '220px' }}>
                                <Code block style={{ fontSize: '11px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', backgroundColor: 'transparent', padding: 0 }}>
                                    {errorDetails}
                                </Code>
                            </ScrollArea>
                        </Paper>
                    </Collapse>
                </Box>
            </Paper>
        </Container>
    );
};

export default ErrorPage;